import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Link2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Connected Accounts card — Google, Facebook, Apple, Email/Password.
 * Users can link multiple OAuth providers from here. Unlink is allowed
 * as long as at least one auth method remains.
 *
 * `children` (optional): rendered below the providers, separated, so callers
 * can colocate a password form inside this same card.
 */
const ConnectedAccounts = ({ isSpanish, children }) => {
  const [accounts, setAccounts] = useState({
    google: false,
    facebook: false,
    apple: false,
    email: false,
  });
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(null);

  const fetchAccounts = useCallback(async () => {
    const { data } = await apiFetch('/api/auth/connected-accounts');
    if (data) setAccounts(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleLinkGoogle = async () => {
    setLinking('google');
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Use custom OAuth if backend is configured; fall back to Emergent.
      let usingCustom = false;
      try {
        const sr = await fetch(`${BACKEND}/api/auth/google/status`);
        const sj = await sr.json();
        usingCustom = !!(sj.enabled && sj.configured);
      } catch { /* fall back */ }

      let popupUrl;
      if (usingCustom) {
        const { data: intent, error: intentErr } = await apiFetch(
          '/api/auth/google/link-intent', { method: 'POST' }
        );
        if (intentErr || !intent?.nonce) {
          toast.error(intentErr || 'Failed to start linking');
          setLinking(null);
          return;
        }
        popupUrl = `${BACKEND}/api/auth/google/login?link_intent=${encodeURIComponent(intent.nonce)}`;
      } else {
        popupUrl = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google/init?redirect_url=${encodeURIComponent(window.location.origin + '/auth/google/callback')}`;
      }

      const popup = window.open(
        popupUrl,
        'google_link',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const handleMessage = async (event) => {
        if (event.data?.type === 'google-link-result') {
          window.removeEventListener('message', handleMessage);
          if (event.data.ok) {
            toast.success(isSpanish ? 'Google conectado' : 'Google account linked!');
            fetchAccounts();
          } else {
            toast.error(event.data.error || (isSpanish ? 'Error al conectar' : 'Link failed'));
          }
          setLinking(null);
          return;
        }
        if (event.data?.type === 'google-auth-callback' && event.data?.session_id) {
          window.removeEventListener('message', handleMessage);
          const { error } = await apiFetch('/api/auth/link/google', {
            method: 'POST',
            body: { session_id: event.data.session_id },
          });
          if (error) {
            toast.error(error);
          } else {
            toast.success(isSpanish ? 'Google conectado' : 'Google account linked!');
            fetchAccounts();
          }
          setLinking(null);
        }
      };
      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setLinking(null);
          window.removeEventListener('message', handleMessage);
        }
      }, 1000);
    } catch {
      toast.error('Failed to start Google linking');
      setLinking(null);
    }
  };

  const handleLinkFacebook = async () => {
    setLinking('facebook');
    try {
      if (!window.FB) {
        toast.error('Facebook SDK not loaded. Please refresh the page.');
        setLinking(null);
        return;
      }
      const loginOpts = {};
      const configId = process.env.REACT_APP_FB_CONFIG_ID;
      if (configId) loginOpts.config_id = configId;
      window.FB.login(async (response) => {
        if (response.authResponse) {
          const { error } = await apiFetch('/api/auth/link/facebook', {
            method: 'POST',
            body: {
              access_token: response.authResponse.accessToken,
              user_id: response.authResponse.userID,
            },
          });
          if (error) {
            toast.error(error);
          } else {
            toast.success(isSpanish ? 'Facebook conectado' : 'Facebook account linked!');
            fetchAccounts();
          }
        }
        setLinking(null);
      }, loginOpts);
    } catch {
      toast.error('Failed to start Facebook linking');
      setLinking(null);
    }
  };

  const handleLinkApple = async () => {
    setLinking('apple');
    try {
      if (!window.AppleID?.auth) {
        toast.error('Apple Sign-In SDK not loaded. Please refresh the page.');
        setLinking(null);
        return;
      }
      // Ensure init has been called
      if (!window.__appleIdInited) {
        const clientId =
          document.querySelector('meta[name="appleid-signin-client-id"]')?.getAttribute('content') ||
          'com.clearcommunicationapp.www';
        const redirectURI =
          document.querySelector('meta[name="appleid-signin-redirect-uri"]')?.getAttribute('content') ||
          'https://www.clearcommunicationapp.com/api/auth/apple/callback';
        window.AppleID.auth.init({
          clientId,
          scope: 'name email',
          redirectURI,
          state: 'ccapp-link-' + Math.random().toString(36).slice(2, 10),
          usePopup: true,
        });
        window.__appleIdInited = true;
      }

      const result = await window.AppleID.auth.signIn();
      const idToken = result?.authorization?.id_token;
      if (!idToken) throw new Error('No id_token from Apple');

      const { error } = await apiFetch('/api/auth/link/apple', {
        method: 'POST',
        body: { id_token: idToken, user: result?.user || null },
      });
      if (error) {
        toast.error(error);
      } else {
        toast.success(isSpanish ? 'Apple conectado' : 'Apple account linked!');
        fetchAccounts();
      }
    } catch (e) {
      if (e?.error !== 'popup_closed_by_user' && e?.error !== 'user_cancelled_authorize') {
        toast.error(isSpanish ? 'No se pudo conectar con Apple' : 'Could not link Apple');
      }
    } finally {
      setLinking(null);
    }
  };

  const handleUnlink = async (provider) => {
    setLinking(provider);
    const { error } = await apiFetch(`/api/auth/unlink/${provider}`, { method: 'POST' });
    if (error) {
      toast.error(error);
    } else {
      toast.success(isSpanish ? `${provider} desconectado` : `${provider} account unlinked`);
      fetchAccounts();
    }
    setLinking(null);
  };

  if (loading) return null;

  const providers = [
    {
      key: 'google',
      label: 'Google',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      ),
      bg: 'bg-white border',
      brandBtn: 'bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]',
      onLink: handleLinkGoogle,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      bg: 'bg-[#1877F2]',
      brandBtn: 'bg-[#1877F2] hover:bg-[#1565D8] text-white',
      onLink: handleLinkFacebook,
    },
    {
      key: 'apple',
      label: 'Apple',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
          <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.49 0-2.534-1.27-3.514-2.61-1.273-1.73-2.244-4.42-2.244-6.97 0-4.06 2.65-6.21 5.265-6.21 1.405 0 2.575.92 3.45.92.832 0 2.143-.97 3.71-.97.61 0 2.79.06 4.21 2.04-.12.07-2.485 1.45-2.485 4.45 0 3.46 3.07 4.62 3.18 4.65z" />
        </svg>
      ),
      bg: 'bg-black',
      brandBtn: 'bg-black hover:bg-zinc-800 text-white',
      onLink: handleLinkApple,
    },
  ];

  return (
    <Card className="mb-6" data-testid="connected-accounts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          {isSpanish ? 'Cuentas Conectadas' : 'Connected Accounts'}
        </CardTitle>
        <CardDescription>
          {isSpanish ? 'Conecta tus cuentas para iniciar sesión de varias formas' : 'Link your accounts to sign in multiple ways'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {providers.map((p, idx) => (
          <React.Fragment key={p.key}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full ${p.bg} flex items-center justify-center`}>{p.icon}</div>
                <div>
                  <p className="font-medium text-sm">{p.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {accounts[p.key] ? (isSpanish ? 'Conectado' : 'Connected') : (isSpanish ? 'No conectado' : 'Not connected')}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant={accounts[p.key] ? 'outline' : 'default'}
                className={accounts[p.key] ? 'border-red-300 text-red-600 hover:bg-red-50' : p.brandBtn}
                onClick={() => (accounts[p.key] ? handleUnlink(p.key) : p.onLink())}
                disabled={linking === p.key}
                data-testid={`link-${p.key}-btn`}
              >
                {linking === p.key ? '…' : accounts[p.key] ? (isSpanish ? 'Desconectar' : 'Disconnect') : (isSpanish ? 'Conectar' : 'Connect')}
              </Button>
            </div>
            {idx < providers.length - 1 && <Separator />}
          </React.Fragment>
        ))}

        <Separator />

        {/* Email/Password — informational, not a link button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium text-sm">{isSpanish ? 'Correo y Contraseña' : 'Email & Password'}</p>
              <p className="text-xs text-muted-foreground">
                {accounts.email
                  ? (isSpanish ? 'Contraseña establecida' : 'Password set')
                  : (isSpanish ? 'Sin contraseña' : 'No password set')}
              </p>
            </div>
          </div>
          {accounts.email ? (
            <Badge variant="secondary" className="text-green-600">
              {isSpanish ? 'Activo' : 'Active'}
            </Badge>
          ) : (
            <a
              href="#password-card"
              className="text-xs text-[#00A896] hover:underline font-medium"
              data-testid="set-password-jumplink"
            >
              {isSpanish ? 'Establecer abajo →' : 'Set below →'}
            </a>
          )}
        </div>

        {/* Optional inline password form — rendered by the parent (SettingsPage)
            so all account/credential controls live in one card. */}
        {children && (
          <>
            <Separator />
            <div id="password-card" data-testid="inline-password-section">
              {children}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectedAccounts;
